/** 公司公告：所有获授权账号可查看自己的公告并独立确认，无需关联员工档案。 */
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Megaphone, Pin } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { toast } from "../../components/ui/sonner";
import { fmtDT } from "../../lib/date";

export default function Announcements() {
  const { api, lang, canPerm } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => zh ? z : e;
  const [params] = useSearchParams();
  const id = params.get("id") || "";
  const [rows, setRows] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [nextOffset, setNextOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async (offset = 0) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/hrms/newsletter", { params: { limit: 25, offset } });
      setRows((prev) => offset ? [...prev, ...(data.rows || [])] : (data.rows || []));
      setNextOffset(offset + (data.rows || []).length);
      setHasMore(!!data.hasMore);
    } catch {
      setError(zh ? "公告加载失败，请稍后重试。" : "Unable to load announcements. Please try again.");
    } finally { setLoading(false); }
  }, [api, zh]);

  useEffect(() => { load(0); }, [load]);
  useEffect(() => {
    let active = true;
    setFeatured(null);
    if (id) api.get(`/hrms/newsletter/${encodeURIComponent(id)}`)
      .then(({ data }) => { if (active) setFeatured(data); })
      .catch(() => { if (active) setError(zh ? "公告不存在或当前账号无权查看。" : "This announcement is unavailable to your account."); });
    return () => { active = false; };
  }, [api, id, zh]);

  const acknowledge = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/hrms/newsletter/${encodeURIComponent(row.id)}/ack`, { version: row.ackVersion || 0 });
      // 确认与提醒已读分开；只在服务端成功记录后更新当前页面。
      const sameVersion = (item) => item?.id === row.id && (item.ackVersion || 0) === (row.ackVersion || 0);
      setRows((list) => list.map((item) => sameVersion(item) ? { ...item, acknowledged: true } : item));
      setFeatured((item) => sameVersion(item) ? { ...item, acknowledged: true } : item);
      toast.success(T("已确认阅读", "Acknowledged"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("确认失败，请重试", "Unable to acknowledge"));
      if (e?.response?.status === 409) {
        load(0);
        // 深链公告不一定在第一页；内容更新后单独刷新它，确保用户能重新阅读并确认。
        if (id === row.id) api.get(`/hrms/newsletter/${encodeURIComponent(id)}`).then(({ data }) => setFeatured(data)).catch(() => setFeatured(null));
      }
    } finally { setBusy(""); }
  };

  const visible = featured ? [featured, ...rows.filter((row) => row.id !== featured.id)] : rows;
  return (
    <main className="mx-auto max-w-5xl space-y-5" data-testid="announcements-page">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-brand-50 p-3 text-brand-600"><Megaphone className="h-5 w-5" /></div>
        <div><h1 className="text-xl font-bold text-slate-900">{T("公司公告", "Announcements")}</h1>
          <p className="mt-1 text-sm text-slate-500">{T("查看发送给你的公告，并在阅读后确认。", "Read and acknowledge announcements sent to you.")}</p></div>
      </div>
      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}
        <button className="ml-3 font-semibold underline" onClick={() => load(0)}>{T("重试", "Retry")}</button></div>}
      {!loading && !error && visible.length === 0 && <div className="card-soft p-8 text-center text-sm text-slate-500">{T("暂无公告", "No announcements")}</div>}
      <div className="space-y-3">
        {visible.map((row) => <article key={row.id} className="card-soft p-4 sm:p-5" data-testid={`announcement-${row.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0"><div className="flex flex-wrap items-center gap-2">
              {row.pinned && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700"><Pin className="h-3 w-3" />{T("置顶", "Pinned")}</span>}
              <h2 className="break-words text-base font-semibold text-slate-900">{row.title}</h2>
            </div><p className="mt-1 text-xs text-slate-500">{row.by || "HR"} · {fmtDT(row.createdAt)}</p></div>
            {row.acknowledged ? <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" />{T("已确认", "Acknowledged")}</span>
              : canPerm("/announcements::ack") && <button data-testid={`announcement-confirm-${row.id}`} disabled={!!busy} onClick={() => acknowledge(row)}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">{busy === row.id ? T("确认中…", "Saving…") : T("确认已阅读", "Acknowledge")}</button>}
          </div>
          {row.content && <p className="mt-4 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{row.content}</p>}
        </article>)}
      </div>
      {loading && <div className="py-4 text-center text-slate-400"><Loader2 className="inline h-5 w-5 animate-spin" /></div>}
      {hasMore && !loading && <div className="text-center"><button className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" onClick={() => load(nextOffset)}>{T("加载更多", "Load more")}</button></div>}
    </main>
  );
}
